import { TELEPHONE_NUMBER_REGEX } from '@cibo/core'
import { ClearOrSaveButtonsFormik, ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import { Collapse, FormControl, FormHelperText, FormLabel, Stack, Typography } from '@mui/material'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import { mergeAll } from 'ramda'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { Loading } from '../../../../components'
import { useAuth, useUpdateUserDetails, useUserDetails } from '../../../../hooks'
import { useOrgUser } from '../../../../queries'
import { filterEmptyStrings } from './filterEmptyStrings'
import { UsdaGrowerDetails, UsdaGrowerDetailsValues } from './types'
import { DEFAULT_MAILING_ADDRESS, UsdaMailingAddressField } from './UsdaMailingAddressField'

/**
 * Assumptions:
 * 1. you will only pass a single resourceId in the resourceIds array
 * 2. there will only ever be a single detail for a given resourceId
 */
export const UsdaGrowerDetailsTaskEditorForm = ({
  detailRequirements,
  resourceIds: [resourceId],
  onClear,
}: ResourceDetailFeatureTaskEditorProps<UsdaGrowerDetails> & { onClear(): void }) => {
  const { t } = useTranslation('@cibo/profile/UsdaGrowerDetailsTaskEditor')
  const details = useUserDetails({ resourceId, detailRequirements })
  const { userId } = useAuth()
  const context = resourceId === userId?.toString() ? 'participant' : 'manager'

  const detail = details.data?.[0]

  const disabled = detail?.immutable || details?.isPending

  return (
    <Form>
      <Stack spacing={2}>
        <Stack>
          <Typography>{t('title')}</Typography>
          <Typography variant="caption">{t('cta')}</Typography>
        </Stack>

        <Stack spacing={1}>
          <FormLabel required>{t('fullNameCta', { context })}</FormLabel>

          <FormControl component="fieldset" variant="standard" disabled={disabled}>
            <Stack direction="row" spacing={2} style={{ width: '100%' }}>
              <Field
                id="firstName"
                name="firstName"
                component={TextField}
                InputProps={{
                  inputProps: {
                    'data-testid': 'firstName',
                    autoComplete: 'section-basics given-name',
                  },
                }}
                maxLength={255}
                placeholder={t('firstName')}
                disabled={disabled}
                fullWidth={true}
              />
              <Field
                id="lastName"
                name="lastName"
                component={TextField}
                InputProps={{
                  inputProps: {
                    'data-testid': 'lastName',
                    autoComplete: 'section-basics family-name',
                  },
                }}
                maxLength={255}
                placeholder={t('lastName')}
                disabled={disabled}
                fullWidth={true}
              />
            </Stack>
          </FormControl>
        </Stack>

        <Stack spacing={1}>
          <FormLabel required>{t('telephoneNumberCta', { context })}</FormLabel>

          <FormControl component="fieldset" variant="standard" disabled={disabled}>
            <Stack direction="row" spacing={2} style={{ width: '100%' }}>
              <Stack spacing={1} style={{ flex: 1 }}>
                <FormLabel>{t('mobile')}</FormLabel>
                <Field
                  id="mobilePhone"
                  name="mobilePhone"
                  component={TextField}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'mobilePhone',
                      autoComplete: 'section-basics tel mobile',
                    },
                  }}
                  maxLength={255}
                  placeholder={t('phoneNumber')}
                  disabled={disabled}
                  fullWidth={true}
                />
              </Stack>
              <Stack spacing={1} style={{ flex: 1 }}>
                <Typography>{t('business')}</Typography>
                <Field
                  id="businessPhone"
                  name="businessPhone"
                  component={TextField}
                  InputProps={{
                    inputProps: {
                      'data-testid': 'businessPhone',
                      autoComplete: 'section-basics tel work',
                    },
                  }}
                  maxLength={255}
                  placeholder={t('phoneNumber')}
                  disabled={disabled}
                  fullWidth={true}
                />
              </Stack>
            </Stack>
          </FormControl>
        </Stack>

        <Stack>
          <Stack>
            <Typography>{t('mailingAddress')}</Typography>
            <Typography variant="caption">{t('mailingAddressDescription')}</Typography>
          </Stack>

          <UsdaMailingAddressField name="mailingAddress" />
        </Stack>

        <ClearOrSaveButtonsFormik
          hasValidDetail={!!detail?.value}
          disabled={disabled}
          onClear={onClear}
          testid="growerDetails"
        />
      </Stack>
    </Form>
  )
}

export const UsdaGrowerDetailsTaskEditor = (
  props: ResourceDetailFeatureTaskEditorProps<UsdaGrowerDetails>
) => {
  const { t } = useTranslation('@cibo/profile/UsdaGrowerDetailsTaskEditor')
  const { onUpdating, onError, onSuccess, detailRequirements } = props
  const resourceId = props.resourceIds[0]

  // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const details = useUserDetails({
    resourceId: props.resourceIds[0],
    detailRequirements: props.detailRequirements,
  })
  const orgUser = useOrgUser(props.resourceIds[0])
  const updateUserDetails = useUpdateUserDetails<UsdaGrowerDetails>()

  const detail = details?.data?.[0]

  const DEFAULT_FORM_VALUES = {
    firstName: orgUser.data?.user.givenName || '',
    lastName: orgUser.data?.user.familyName || '',
    mobilePhone: '',
    businessPhone: '',
    mailingAddress: DEFAULT_MAILING_ADDRESS,
  }

  const schema = useMemo(
    () =>
      yup
        .object({
          firstName: yup.string().min(2).required(t('required')),
          lastName: yup.string().min(2).required(t('required')),

          businessPhone: yup.string().matches(TELEPHONE_NUMBER_REGEX, t('needsValidPhoneNumber')),
          mobilePhone: yup.string().matches(TELEPHONE_NUMBER_REGEX, t('needsValidPhoneNumber')),

          mailingAddress: yup.object(),
        })
        .test('phoneNumber', t('phoneRequired'), function (value) {
          const phoneNumberPresent = !!value?.businessPhone || !!value?.mobilePhone

          if (!phoneNumberPresent) {
            return this.createError({
              path: 'mobilePhone',
              message: t('phoneRequired'),
            })
          }

          return true
        }),
    [t]
  )

  const year = detailRequirements[0].year

  const onSubmit = useCallback(
    (values: any, { setSubmitting }: any) => {
      const filtered: any = filterEmptyStrings(values as object)

      onUpdating?.()
      updateUserDetails
        .mutateAsync({
          resourceId,
          details: [
            {
              traitId: 'usdaGrowerDetails',
              year,
              value: { ...filtered },
            } as UsdaGrowerDetails,
          ],
        })
        .then(onSuccess)
        .catch(onError)
        .finally(() => setSubmitting(false))
    },
    [onUpdating, onError, onSuccess, resourceId, year]
  )

  const onClear = useCallback(() => {
    onUpdating?.()
    updateUserDetails
      .mutateAsync({
        resourceId,
        details: [
          {
            traitId: 'usdaGrowerDetails',
            year,
          } as UsdaGrowerDetails,
        ],
      })
      .then(onSuccess)
      .catch(onError)
  }, [onUpdating, onError, onSuccess, resourceId, year])

  if (details.isPending) return <Loading />

  const initialValues = mergeAll([
    DEFAULT_FORM_VALUES,
    (detail?.value as UsdaGrowerDetailsValues) || {},
  ])

  return (
    <Stack spacing={2}>
      <Formik<UsdaGrowerDetailsValues>
        // @ts-ignore How to allow empty strings for these initial values?
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={schema}
        validateOnMount
        enableReinitialize
      >
        <UsdaGrowerDetailsTaskEditorForm {...props} onClear={onClear} />
      </Formik>

      <Collapse in={updateUserDetails.isError}>
        <FormHelperText>{updateUserDetails.error?.response?.data?.message}</FormHelperText>
      </Collapse>
    </Stack>
  )
}
